import { BookOpen, CheckCircle2, Mail, Send } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { cn } from '../lib/format';
import { useSession } from '../lib/session';
import { LEGAL } from '../pages/legal/meta';
import { CONTACT_TOPICS, useMarketingCopy, type ContactTopic } from './content';
import { Breadcrumbs, MarketingShell } from './MarketingLayout';
import { useLocalePath, usePageLocale } from './PublicRoute';

const input =
  'w-full rounded-xl border border-zinc-300 bg-white px-3.5 py-2.5 text-zinc-900 shadow-sm outline-none transition placeholder:text-zinc-400 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15';

/** Support email and a feedback form; messages go to the support inbox (POST /api/contact). */
export function ContactPage() {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const c = copy.contact;
  const lp = useLocalePath();
  const user = useSession((s) => s.user);
  const [form, setForm] = useState({
    name: '',
    email: '',
    topic: 'support' as ContactTopic,
    message: '',
    website: '',
  });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  // Signed-in visitors don't have to type their address
  const email = form.email || user?.email || '';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('sending');
    try {
      await api('/api/contact', {
        method: 'POST',
        workspace: false,
        body: { ...form, email, locale },
      });
      setState('sent');
    } catch {
      setState('error');
    }
  };

  return (
    <MarketingShell>
      <section className="mx-auto max-w-5xl px-6 pt-8 pb-24">
        <Breadcrumbs items={[{ label: copy.ui.home, to: lp('/') }, { label: c.label }]} />
        <div className="mt-10 inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
          {c.eyebrow}
        </div>
        <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-zinc-900 md:text-5xl">
          {c.h1}
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-600">{c.lead}</p>

        <div className="mt-12 grid gap-8 lg:grid-cols-[3fr_2fr]">
          <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-zinc-200 sm:p-8">
            {state === 'sent' ? (
              <div className="py-10 text-center" data-testid="contact-sent">
                <CheckCircle2 className="mx-auto size-12 text-emerald-500" />
                <h2 className="mt-4 text-2xl font-bold text-zinc-900">{c.form.successTitle}</h2>
                <p className="mt-2 text-zinc-600">{c.form.successText}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <h2 className="text-xl font-bold text-zinc-900">{c.form.title}</h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-zinc-700">
                      {c.form.name}
                    </span>
                    <input
                      name="name"
                      autoComplete="name"
                      maxLength={100}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={input}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-zinc-700">
                      {c.form.email}
                    </span>
                    <input
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      maxLength={200}
                      value={email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={input}
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-zinc-700">
                    {c.form.topic}
                  </span>
                  <select
                    name="topic"
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value as ContactTopic })}
                    className={input}
                  >
                    {CONTACT_TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {c.form.topics[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-zinc-700">
                    {c.form.message}
                  </span>
                  <textarea
                    name="message"
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={6}
                    placeholder={c.form.messagePlaceholder}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={cn(input, 'resize-y')}
                  />
                </label>
                {/* Left empty by people; bots that fill every field are ignored by the server */}
                <input
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                {state === 'error' && (
                  <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700 ring-1 ring-rose-100">
                    {c.form.error.replace('{email}', LEGAL.email)}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={state === 'sending'}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white shadow-lg shadow-brand-500/25 hover:bg-brand-700 disabled:opacity-60"
                  >
                    <Send className="size-4" />
                    {state === 'sending' ? c.form.sending : c.form.send}
                  </button>
                  <p className="text-xs text-zinc-500">{c.form.privacy}</p>
                </div>
              </form>
            )}
          </div>

          <aside className="space-y-5">
            <a
              href={`mailto:${LEGAL.email}`}
              className="block rounded-3xl bg-gradient-to-br from-indigo-50 to-white p-6 ring-1 ring-indigo-100 transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              <Mail className="size-6 text-brand-600" />
              <h2 className="mt-3 font-semibold text-zinc-900">{c.emailTitle}</h2>
              <p className="mt-1 text-sm text-zinc-600">{c.emailText}</p>
              <p className="mt-3 font-semibold text-brand-700">{LEGAL.email}</p>
            </a>
            <a
              href="/docs"
              className="block rounded-3xl bg-gradient-to-br from-sky-50 to-white p-6 ring-1 ring-sky-100 transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              <BookOpen className="size-6 text-sky-600" />
              <h2 className="mt-3 font-semibold text-zinc-900">{c.docsTitle}</h2>
              <p className="mt-1 text-sm text-zinc-600">{c.docsText}</p>
            </a>
          </aside>
        </div>
      </section>
    </MarketingShell>
  );
}
